import { useEffect, useRef, useState } from 'react'
import { Upload, X } from 'lucide-react'
import { HeroItem, RedirectType } from 'interfaces/widget-types'
import { useQuery, useQueryClient } from 'react-query'
import { HttpRequest } from 'services/_http'
import { InformationCircleIcon } from '@heroicons/react/24/outline'
import { fetchEpisodesSearchApi } from 'hooks/episode'
import { fetchShowSearchApi } from 'hooks/shows-master'
import { fetchCollectionSearchApi } from 'hooks/collection'
import { fetchCategorySearchApi } from 'hooks/category-manage'
import { getRelativeIconPath, isValidUrl } from 'services/util'


/* ---------- Types ---------- */

interface RedirectOption {
    value: RedirectType
    label: string
}

/**
 * 🔑 THIS IS WHERE YOUR FormData INTERFACE GOES
 */
interface FormData {
    name?: string,
    image?: string
    thumbnail?: string
    imageFile: File | null
    imagePreview: string | null
    redirectTo: RedirectType
    video_id?: string
    redirectionId: string
}

interface FormErrors {
    image?: string
    redirectionId?: string
}

interface AddItemModalProps {
    isOpen: boolean
    onClose: () => void
    onAdd: (data: Omit<HeroItem, 'id'>) => void
    selectedWidget: string
    redirectOptions: RedirectOption[]
    error?: any
}

const shouldShowImageUpload = (redirectTo: RedirectType) =>
    redirectTo === 'Category' || redirectTo === 'Other' || redirectTo === 'Web View'

/* ---------- Helpers ---------- */

const createInitialForm = (redirectOptions: RedirectOption[]): FormData => ({
    image: '',
    imageFile: null,
    imagePreview: null,
    redirectTo: redirectOptions?.[0]?.value ?? 'Episode',
    redirectionId: '',
})


const searchApiMap = {
    Episode: fetchEpisodesSearchApi,
    Show: fetchShowSearchApi,
    Collection: fetchCollectionSearchApi,
    Category: fetchCategorySearchApi,
} as const


const useRedirectSearch = (
    type: RedirectType,
    search: string
) => {
    return useQuery({
        queryKey: ['redirect-search', type, search],
        queryFn: async () => {
            if (type === 'Other' || type === 'Web View' || !search) return []

            const api = searchApiMap[type as keyof typeof searchApiMap]

            const res = await api(search)
            return res ?? []
        },
        enabled: type !== 'Other' && type !== 'Web View' && search.length >= 2,
        staleTime: 60_000,
    })
}

/* ---------- Component ---------- */

const AddItemModal = ({
    isOpen,
    onClose,
    onAdd,
    selectedWidget,
    redirectOptions,
}: AddItemModalProps) => {
    const [formData, setFormData] = useState<FormData>(
        createInitialForm(redirectOptions)
    )

    const [errors, setErrors] = useState<FormErrors>({})
    const [searchText, setSearchText] = useState('')
    const [selectedItemName, setSelectedItemName] = useState('')
    const [showSearchResults, setShowSearchResults] = useState(false)
    const queryClient = useQueryClient()
    const fileInputRef = useRef<HTMLInputElement | null>(null)

    /* Cleanup image preview */
    useEffect(() => {
        return () => {
            if (formData.imagePreview) {
                URL.revokeObjectURL(formData.imagePreview)
            }
        }
    }, [formData.imagePreview])

    // Search Query - only search when user is typing (not when item is selected)
    const { data: searchItems = [], isLoading } = useRedirectSearch(
        formData.redirectTo,
        showSearchResults ? searchText : ''
    )

    // Reset search when type changes
    useEffect(() => {
        setSearchText('')
        setSelectedItemName('')
        setShowSearchResults(false)
        setFormData(prev => ({
            ...prev,
            redirectionId: '',
        }))
        setErrors(prev => ({
            ...prev,
            redirectionId: undefined,
        }))
    }, [formData.redirectTo])

    // Handle item selection from search results
    const handleItemSelect = (item: any) => {
        const itemName = item.name || item.title || item.id
        setSelectedItemName(itemName)
        setSearchText(itemName)
        setShowSearchResults(false)
        setFormData(prev => ({
            ...prev,
            name: item.name || item.title,
            thumbnail: item.thumbnail,
            redirectionId: item.id,
        }))
        setErrors(prev => ({
            ...prev,
            redirectionId: undefined,
        }))
    }

    // Handle search input change
    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value
        setSearchText(value)

        // Show results if user is typing (length >= 2) and hide if cleared
        if (value.length >= 2) {
            setShowSearchResults(true)
        } else {
            setShowSearchResults(false)
            // Clear selection if search is cleared
            if (value === '') {
                setSelectedItemName('')
                setFormData(prev => ({
                    ...prev,
                    redirectionId: '',
                }))
            }
        }
    }

    // Image upload handlers (commented out - image upload is disabled)
    const handleRemoveImage = (
        e: React.MouseEvent<HTMLButtonElement>
    ) => {
        e.preventDefault()
        e.stopPropagation()

        setFormData((prev) => ({
            ...prev,
            imageFile: null,
            imagePreview: null,
            thumbnail: '',
        }))

        // 🔥 allow same image to be selected again
        if (fileInputRef.current) {
            fileInputRef.current.value = ''
        }

        setErrors({})
    }


    const getEpisode = async (formData: any) => {
        try {
            if (formData.redirectTo === 'Episode' && formData.redirectionId) {
                const episode = await queryClient.fetchQuery({
                    queryKey: ['episode-deails-fetch', formData.redirectionId],
                    queryFn: () =>
                        HttpRequest({
                            url: `/episodes/${formData.redirectionId}`,
                            method: 'GET',
                        }),
                })
                const video_id = episode?.data?.results?.video_id || ''
                formData.video_id = video_id
                return video_id
            }
            return ''
        } catch (error) {
            return false
        }

    }

    const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0]
        if (!file) return

        if (file.size > 5 * 1024 * 1024) {
            setErrors({ image: 'File size must be under 5MB' })
            return
        }
        const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp']
        if (!allowedTypes.includes(file.type)) {
            setErrors({ image: 'Only JPG, JPEG, PNG, webp allowed' })
            return
        }

        setFormData((prev) => ({
            ...prev,
            thumbnail: file.name,
            imageFile: file,
            imagePreview: URL.createObjectURL(file),
        }))

        setErrors({})
    }

    const validateForm = (): boolean => {
        const newErrors: FormErrors = {}

        // if (!formData.thumbnail) newErrors.image = 'Image is required'

        if (!formData.redirectionId)
            newErrors.redirectionId = 'Redirection ID / Url is required'

        if (formData.redirectTo === 'Other' || formData.redirectTo === 'Web View') {
            if (!isValidUrl(formData.redirectionId)) {
                newErrors.redirectionId = 'Invalid Url'
            }
        }

        if (formData.redirectTo === 'Episode' && formData.redirectionId) {
            if (!formData.video_id) {
                newErrors.redirectionId = 'Please add valid Redirection Id'
            }
        }

        // UUID validation for All
        // if (formData.redirectTo === 'Episode' || formData.redirectTo === 'Collection' || formData.redirectTo === 'Show') {
        //     const uuidRegex =
        //         /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i

        //     if (!uuidRegex.test(formData.redirectionId)) {
        //         newErrors.redirectionId = 'Redirection ID must be a valid UUID'
        //     }
        // }

        setErrors(newErrors)
        return Object.keys(newErrors).length === 0
    }

    const handleAdd = async () => {
        setErrors({})
        const video_id = await getEpisode(formData)
        formData.video_id = video_id ? video_id : ''
        if (!validateForm()) return

        const isWebview = formData.redirectTo === 'Web View'
        const redirectionType = (formData.redirectTo).toUpperCase()

        // Prepare data for adding to carousel
        // Note: imageFile and thumbnail are optional (image upload is disabled)
        onAdd({
            name: formData.name || '',
            thumbnail: getRelativeIconPath(formData.imagePreview || formData.thumbnail || ''),
            redirection_to: isWebview ? formData.redirectionId : redirectionType,
            redirection_id: formData.redirectionId,
            redirection_type: isWebview ? 'WEB_VIEW' : redirectionType,
            is_webview: isWebview,
            webview_url: isWebview ? formData.redirectionId : '',
            imageFile: formData.imageFile || null,
            video_id: formData?.video_id || '',
        } as any)

        handleClose()
    }

    const handleClose = () => {
        setFormData(createInitialForm(redirectOptions))
        setErrors({})
        setSearchText('')
        setSelectedItemName('')
        setShowSearchResults(false)
        onClose()
    }

    if (!isOpen) return null

    return (
        <div onPointerDown={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => e.stopPropagation()} className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 !mt-0">
            <div className="bg-white dark:bg-slate-800 rounded-lg p-6 w-96 shadow-lg">
                {/* Header */}
                <div className="flex justify-between items-center mb-4">
                    <h3 className="text-[16px] font-semibold dark:text-gray-100">
                        Add Item to {selectedWidget}
                    </h3>
                    <button onClick={handleClose} className='dark:text-white'>
                        <X size={20} />
                    </button>
                </div>

                {/* Image Upload */}
                {shouldShowImageUpload(formData.redirectTo) &&
                <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Select Image <span className="text-red-700">*</span>
                        {/* Tooltip */}
                        <div className="relative group mr-auto ml-1 mt-0 inline-block align-[sub]">
                            <InformationCircleIcon className="w-4 h-4 text-slate-500 cursor-pointer" />

                            <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-64 rounded-md bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 p-3 text-xs text-slate-700 dark:text-slate-200 shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-150 z-50
 ">
                                Recommended Size - Under 5 MB. Supports JPG/PNG.
                            </div>
                        </div>

                    </label>

                    <div className="border-2 border-dashed rounded-lg p-4 hover:border-blue-400 transition">
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept="image/*"
                            onChange={handleImageChange}
                            className="hidden"
                            id="imageUpload"
                        />


                        <label
                            htmlFor="imageUpload"
                            className="flex flex-col items-center justify-center cursor-pointer"
                        >
                            {formData.imagePreview ? (
                                <div className="relative inline-block">
                                    <img
                                        src={formData.imagePreview}
                                        alt="Preview"
                                        className="w-30 h-20 object-cover rounded-md"
                                    />

                                    {/* ❌ Remove image */}
                                    <button
                                        type="button"
                                        onClick={handleRemoveImage}
                                        className="
                                        absolute -top-2 -right-2
                                        bg-black/70 hover:bg-black
                                        text-white
                                        rounded-full
                                        w-5 h-5
                                        flex items-center justify-center
                                        transition
                                        z-10
                                    "
                                        title="Remove image"
                                    >
                                        <X size={12} />
                                    </button>
                                </div>
                            ) : (

                                <>
                                    <Upload
                                        className="text-gray-400 mb-2"
                                        size={28}
                                    />
                                    <p className="text-sm text-gray-500">
                                        Click to upload image
                                    </p>
                                    <p className="text-xs text-gray-400">
                                        JPG / PNG · Max 5MB
                                    </p>
                                </>
                            )}
                        </label>
                    </div>

                    {errors.image && (
                        <p className="text-sm text-red-500 mt-1">
                            {errors.image}
                        </p>
                    )}
                </div>
}

                {/* Redirect To */}
                <label className="block text-sm font-medium mt-4 dark:text-gray-300 mb-2">
                    Redirect To <span className="text-red-700">*</span>
                    {/* Tooltip */}
                    <div className="relative group mr-auto ml-1 mt-0 inline-block align-[sub]">
                        <InformationCircleIcon className="w-4 h-4 text-slate-500 cursor-pointer" />

                        <div className="absolute bottom-full left-1/3 -translate-x-1/3 mb-2 w-64 rounded-md bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 p-3 text-xs text-slate-700 dark:text-slate-200 shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-150 z-50
 ">
                            Dropdown to choose from episode/ show/ collection/ category
                        </div>
                    </div>
                </label>
                <select
                    value={formData.redirectTo}
                    onChange={(e) =>
                        setFormData((prev) => ({
                            ...prev,
                            redirectTo: e.target.value as RedirectType,
                        }))
                    }
                    className="w-full border px-3 py-2 rounded dark:bg-slate-700 dark:text-white"
                >
                    {redirectOptions.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                            {opt.label}
                        </option>
                    ))}
                </select>

                {/* Redirection ID */}
                {formData.redirectTo !== 'Other' && formData.redirectTo !== 'Web View' && <label className="block text-sm font-medium mt-4 dark:text-gray-300 mb-2">
                    Search Item <span className="text-red-700">*</span>

                    {/* Tooltip */}
                    <div className="relative group mr-auto ml-1 mt-0 inline-block align-[sub]">
                        <InformationCircleIcon className="w-4 h-4 text-slate-500 cursor-pointer" />

                        <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-64 rounded-md bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 p-3 text-xs text-slate-700 dark:text-slate-200 shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-150 z-50">
                            Search and select an item based on the chosen "Redirect To" option 
                        </div>
                    </div>
                </label>}
                {formData.redirectTo === 'Other' && <label className="block text-sm font-medium mt-4 dark:text-gray-300 mb-2">
                    URL <span className="text-red-700">*</span>
                </label>}
                {formData.redirectTo === 'Web View' && <label className="block text-sm font-medium mt-4 dark:text-gray-300 mb-2">
                    Web URL <span className="text-red-700">*</span>
                </label>}

                {/* <input
                    type="text"
                    value={formData.redirectionId}
                    onChange={(e) =>
                        setFormData((prev) => ({
                            ...prev,
                            redirectionId: e.target.value,
                        }))
                    }
                    className="w-full border px-3 py-2 rounded dark:bg-slate-700 dark:text-white"
                /> */}

                {/* Search for redirection types */}
                {formData.redirectTo !== 'Other' && formData.redirectTo !== 'Web View' && (
                    <div className="relative">
                        <input
                            type="text"
                            placeholder={`Search ${formData.redirectTo.toLowerCase()}...`}
                            value={searchText}
                            onChange={handleSearchChange}
                            onFocus={() => {
                                if (searchText.length >= 2) {
                                    setShowSearchResults(true)
                                }
                            }}
                            className="w-full border px-3 py-2 rounded mb-2 dark:bg-slate-700 dark:text-white"
                        />

                        {/* Search Results Dropdown */}
                        {showSearchResults && searchText.length >= 2 && (
                            <div className="absolute z-10 w-full max-h-40 overflow-auto border rounded bg-white dark:bg-slate-700 shadow-lg">
                                {isLoading && (
                                    <div className="p-2 text-sm text-gray-500 dark:text-gray-400">
                                        Loading...
                                    </div>
                                )}

                                {!isLoading && searchItems.length > 0 && (
                                    <>
                                        {searchItems.map((item: any) => (
                                            <div
                                                key={item.id}
                                                onClick={() => handleItemSelect(item)}
                                                className={`p-2 cursor-pointer hover:bg-gray-100 dark:hover:bg-slate-600 transition-colors ${formData.redirectionId === item.id
                                                        ? 'bg-blue-100 dark:bg-blue-900'
                                                        : ''
                                                    }`}
                                            >
                                                <div className="text-sm text-gray-700 dark:text-gray-200">
                                                    {item.name || item.title || item.id}
                                                </div>
                                            </div>
                                        ))}
                                    </>
                                )}

                                {!isLoading && searchText.length >= 2 && searchItems.length === 0 && (
                                    <div className="p-2 text-sm text-gray-500 dark:text-gray-400">
                                        No results found
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Show selected item indicator */}
                        {selectedItemName && !showSearchResults && (
                            <p className="text-xs text-green-600 dark:text-green-400 mt-1 mb-2">
                                Selected: {selectedItemName}
                            </p>
                        )}
                    </div>
                )}

                {/* URL Input for Other and Web View options */}
                {(formData.redirectTo === 'Other' || formData.redirectTo === 'Web View') && (
                    <input
                        type="url"
                        value={formData.redirectionId}
                        onChange={(e) =>
                            setFormData((prev) => ({
                                ...prev,
                                redirectionId: e.target.value,
                            }))
                        }
                        placeholder="https://example.com"
                        className="w-full border px-3 py-2 rounded dark:bg-slate-700 dark:text-white"
                    />
                )}


                {errors.redirectionId && (
                    <p className="text-sm text-red-500">
                        {errors.redirectionId}
                    </p>
                )}

                {/* Actions */}
                <div className="flex gap-2 mt-6">
                    <button
                        onClick={handleAdd}
                        className="flex-1 bg-blue-600 text-white py-2 rounded"
                    >
                        Add
                    </button>
                    <button
                        onClick={handleClose}
                        className="flex-1 bg-gray-300 py-2 rounded"
                    >
                        Cancel
                    </button>
                </div>
            </div>
        </div>
    )
}

export default AddItemModal
